import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import type { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import type { DropdownUuidQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-uuid-query.dto';
import type { PaginationFindInterface } from '@zaoblako/shared/common/dtos/pagination-find.dto';
import { MAX_PAGINATION_LIMIT, DEFAULT_LIMIT, DEFAULT_OFFSET } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { chunkIds, isSameId } from '@zaoblako/web/shared/helpers/include-ids-pagination.helper';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNil } from 'lodash';

export type DropdownQueryInterface<T extends string | number> = T extends string
  ? DropdownUuidQueryInterface
  : DropdownBaseQueryInterface;

@Component({
  standalone: true,
  imports: [CommonModule],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export abstract class BaseMultiSelectComponent<T extends { id: string | number; }> implements OnInit, OnChanges, OnDestroy {
  @ViewChild('multiSelectComponent', { static: true }) public multiSelectComponent: MultiSelectComponent;

  @Input() public entities: T[] = [];
  public queryParams: DropdownQueryInterface<T['id']> = {
    limit: 20,
    offset: DEFAULT_OFFSET
  };

  @Input() public loading = false;
  public paginationLoading = false;
  public count = 0;

  /**
   * True while pending selected IDs from the parent are not yet resolved into entities
   * (init / registry load / hydrate). Bind to `crm-multi-select` `[loading]`.
   */
  public get selectionLoading(): boolean {
    if (!this.pendingSelectedIds.length) {
      return false;
    }

    if (!this.initCompleted || this.applyingPendingSelection || !!this.hydrationSub) {
      return true;
    }

    const currentIds = this._selectedEntities.map((entity) => entity.id);
    return !this.hasSameIdSet(this.pendingSelectedIds, currentIds);
  }

  @Output() public selectedEntitiesChange = new EventEmitter<T[]>();
  @Output() public openedChange = new EventEmitter<boolean>();
  @Output() public panelHide = new EventEmitter<T[]>();

  @Input() public customSearch = false;
  public abstract availableModule: string;
  /** Stable cache bucket for {@link DropdownEntityCacheService} (e.g. `'user'`, `'contragent'`). */
  protected abstract readonly entityCacheKind: string;

  @Input() public includeIds: T['id'][] = [];
  @Input() public excludeIds: T['id'][] = [];

  @Input() public isError = false;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public itemStyleClass: string;
  @Input() public label: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public showClear = true;
  @Input() public placeholder = '\u00A0';
  @Input() public isFloatLabel = true;

  public readonly cdRef = inject(ChangeDetectorRef);
  public readonly globalService = inject(GlobalService);
  protected readonly entityCache = inject(DropdownEntityCacheService);

  protected castToNumber = true;

  protected initCompleted = false;
  private applyingPendingSelection = false;
  private searchSub?: Subscription;
  private hydrationSub?: Subscription;
  private registryLoadSub?: Subscription;
  private pendingSelectedIds: T['id'][] = [];
  /** Original selected objects from parent (GET payload); fallback when registry hydrate misses. */
  private pendingSelectedEntities: T[] = [];
  private scrollElement: HTMLElement | null = null;

  public abstract findWithPagination(
    queryParams: Partial<DropdownQueryInterface<T['id']>>
  ): Observable<PaginationFindInterface<T>>;

  public _selectedEntities: T[] = [];

  @Input() public set selectedEntities(selectedEntities: T[]) {
    const entities = selectedEntities ?? [];
    const next = this.castEntityIds(entities.map((entity) => entity.id));

    // Ignore parent echoing an empty wipe back while we still apply GET selection
    if (
      !next.length &&
      this.pendingSelectedIds.length &&
      (!this.initCompleted || this.applyingPendingSelection)
    ) {
      return;
    }

    this.pendingSelectedIds = next;
    this.pendingSelectedEntities = [...entities];

    if (!this.initCompleted) {
      return;
    }

    if (!this.customSearch) {
      const missingIds = entities
        .filter((selected) => !this.entities.some((entity) => isSameId(entity.id, selected.id)))
        .map((selected) => selected.id);

      if (missingIds.length) {
        this.hydrateEntitiesByIds(this.castEntityIds(missingIds), () => {
          this.applyPendingSelection(next);
        });
        return;
      }
    }

    this.mergePendingEntitiesIntoEntities();
    this.setSelectedEntitiesSilently(entities);
  }

  @Input() public set setSelectedEntityIds(ids: T['id'][]) {
    const next = this.castEntityIds(ids ?? []);
    if (this.hasSameIdSet(next, this.pendingSelectedIds)) {
      return;
    }

    this.pendingSelectedIds = next;
    this.pendingSelectedEntities = [];

    if (!this.initCompleted) {
      this.cdRef.markForCheck();
      return;
    }

    if (!next.length) {
      this.setSelectedEntitiesSilently([]);
      return;
    }

    this.syncSelectionFromPending();
  }

  public ngOnInit() {
    queueMicrotask(() => this.initLoad());
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.excludeIds || changes.includeIds) {
      this.firstLoad();
    }
  }

  public ngOnDestroy() {
    this.unbindScrollPagination();
    this.searchSub?.unsubscribe();
    this.hydrationSub?.unsubscribe();
    this.registryLoadSub?.unsubscribe();
  }

  public onSelect(entities: T[]) {
    if (this.shouldIgnoreSelectionChange(entities)) {
      return;
    }

    this._selectedEntities = entities ?? [];
    this.pendingSelectedIds = this._selectedEntities.map((entity) => entity.id);
    this.pendingSelectedEntities = [...this._selectedEntities];
    this.entityCache.putEntities(this.entityCacheKind, this._selectedEntities);
    this.selectedEntitiesChange.emit(this._selectedEntities);
  }

  /**
   * Ignore spurious empty clears during init or while applying pending selection
   * so two-way bindings do not wipe parent GET data (PrimeNG often emits [] after options sync).
   */
  protected shouldIgnoreSelectionChange(entities: T[] | null | undefined): boolean {
    const next = entities ?? [];
    if (next.length || !this.pendingSelectedIds.length) {
      return false;
    }

    // Empty wipe while we still expect a pending selection
    return !this.initCompleted || this.applyingPendingSelection;
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => this.bindScrollPagination(), 100);
    }
  }

  public onPanelHide(entities: T[]) {
    this.unbindScrollPagination();
    this.panelHide.emit(entities);
  }

  private readonly onScrollPagination = (): void => {
    const el = this.scrollElement;
    if (!el) {
      return;
    }

    if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight - 1) {
      this.loadMoreEntities();
    }
  };

  private bindScrollPagination(attempt = 0): void {
    const el = this.getPanelScrollElement();
    if (!el) {
      if (attempt < 8) {
        setTimeout(() => this.bindScrollPagination(attempt + 1), 100);
      }
      return;
    }

    if (this.scrollElement === el) {
      return;
    }

    this.unbindScrollPagination();
    this.scrollElement = el;
    el.addEventListener('scroll', this.onScrollPagination, { passive: true });
  }

  private unbindScrollPagination(): void {
    if (this.scrollElement) {
      this.scrollElement.removeEventListener('scroll', this.onScrollPagination);
      this.scrollElement = null;
    }
  }

  private getPanelScrollElement(): HTMLElement | null {
    const multiSelect = this.multiSelectComponent?.multiSelect;
    const overlayEl = multiSelect?.overlayViewChild?.overlayViewChild?.nativeElement as HTMLElement | undefined;

    return (
      (multiSelect?.itemsWrapper as HTMLElement | undefined) ??
      (overlayEl?.querySelector('.p-multiselect-items-wrapper') as HTMLElement | null) ??
      null
    );
  }

  private loadMoreEntities(): void {
    if (
      this.customSearch ||
      this.entities.length >= this.count ||
      this.loading ||
      this.paginationLoading
    ) {
      return;
    }

    this.paginationLoading = true;
    this.queryParams.offset = (this.queryParams.offset ?? DEFAULT_OFFSET) + (this.queryParams.limit ?? DEFAULT_LIMIT);
    this.cdRef.markForCheck();

    this.findWithPagination(this.buildRegistryQuery()).subscribe({
      next: (data) => {
        if (!this.customSearch) {
          this.entities = this.entities.concat(
            data.items.filter((item) => !this.entities.some((entity) => isSameId(entity.id, item.id)))
          );
          this.count = data.count;
        }
        this.paginationLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.paginationLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.searchSub?.unsubscribe();
      this.loading = true;
      this.queryParams.search = name?.length ? name : undefined;
      this.queryParams.offset = DEFAULT_OFFSET;
      this.cdRef.markForCheck();

      this.searchSub = this.findWithPagination(this.buildRegistryQuery(0)).subscribe({
        next: (data) => {
          if (!this.customSearch) {
            const missingSelected = this._selectedEntities.filter(
              (entity) => !data.items.some((item) => isSameId(item.id, entity.id))
            );
            this.entities = [...data.items, ...missingSelected];
            this.count = data.count;
          }
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.multiSelectComponent?.multiSelect?.filterValue);
  }

  protected augmentHydrationQuery(
    query: Partial<DropdownQueryInterface<T['id']>>
  ): Partial<DropdownQueryInterface<T['id']>> {
    return query;
  }

  private buildHydrationQuery(chunk: T['id'][]): Partial<DropdownQueryInterface<T['id']>> {
    return this.augmentHydrationQuery({
      includeIds: chunk,
      limit: chunk.length,
      offset: DEFAULT_OFFSET
    } as Partial<DropdownQueryInterface<T['id']>>);
  }

  private hydrateEntitiesByIds(ids: T['id'][], onComplete: () => void): void {
    if (!ids.length) {
      onComplete();
      return;
    }

    this.hydrationSub?.unsubscribe();
    this.hydrationSub = this.entityCache
      .loadByIds(this.entityCacheKind, ids, (missing) =>
        forkJoin(
          chunkIds(missing as T['id'][], MAX_PAGINATION_LIMIT).map((chunk) =>
            this.findWithPagination(this.buildHydrationQuery(chunk))
          )
        ).pipe(map((pages) => pages.flatMap((page) => page.items)))
      )
      .subscribe({
        next: (items) => {
          if (!this.customSearch) {
            this.entities = this.entities.concat(
              items.filter((item) => !this.entities.some((entity) => isSameId(entity.id, item.id)))
            );
          }
          this.hydrationSub = undefined;
          onComplete();
          this.cdRef.markForCheck();
        },
        error: () => {
          this.hydrationSub = undefined;
          onComplete();
          this.cdRef.markForCheck();
        }
      });
    this.cdRef.markForCheck();
  }

  private initLoad(): void {
    if (!this.canLoadRegistry()) {
      this.completeInit();
      return;
    }

    this.loadRegistryPage();
  }

  public firstLoad() {
    if (this.loading) {
      return;
    }

    this.initLoad();
  }

  public loadRegistryPage(): void {
    if (!this.canLoadRegistry()) {
      this.completeInit();
      return;
    }

    if (this.loading) {
      return;
    }

    this.registryLoadSub?.unsubscribe();
    this.loading = true;
    this.queryParams.search = undefined;
    this.queryParams.offset = DEFAULT_OFFSET;
    this.cdRef.markForCheck();

    const fingerprint = this.buildFirstPageFingerprint();
    this.registryLoadSub = this.entityCache
      .loadFirstPage(this.entityCacheKind, fingerprint, () => this.findWithPagination(this.buildRegistryQuery(0)))
      .subscribe({
        next: (data) => {
          // Local options may arrive while this page is in flight. Do not replace them.
          if (!this.customSearch) {
            const preserved = this.entities.filter(
              (entity) => !data.items.some((item) => isSameId(item.id, entity.id))
            );
            this.entities = [...data.items, ...preserved];
            this.count = data.count;
          }
          this.loading = false;
          this.completeInit();
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.completeInit();
          this.cdRef.markForCheck();
        }
      });
  }

  private buildRegistryQuery(offset = this.queryParams.offset): Partial<DropdownQueryInterface<T['id']>> {
    return {
      ...this.queryParams,
      offset,
      includeIds: this.includeIds?.length ? this.includeIds : undefined,
      excludeIds: this.excludeIds?.length ? this.excludeIds : undefined
    };
  }

  /** Extra query dims not present on {@link buildRegistryQuery} (e.g. clientIds). */
  protected getRegistryCacheContext(): Record<string, unknown> {
    return {};
  }

  private buildFirstPageFingerprint(): string {
    return this.entityCache.fingerprint({
      ...(this.buildRegistryQuery(0) as Record<string, unknown>),
      ...this.getRegistryCacheContext()
    });
  }

  private canLoadRegistry(): boolean {
    return (
      !this.customSearch &&
      !!this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.[this.availableModule]?.enabled
    );
  }

  private completeInit(): void {
    if (this.initCompleted) {
      return;
    }

    this.initCompleted = true;
    this.syncSelectionFromPending();
  }

  private syncSelectionFromPending(): void {
    const ids = this.pendingSelectedIds;
    const currentIds = this._selectedEntities.map((entity) => entity.id);
    if (this.hasSameIdSet(ids, currentIds)) {
      return;
    }

    if (!ids.length) {
      this.setSelectedEntitiesSilently([]);
      return;
    }

    this.mergePendingEntitiesIntoEntities();

    const missingIds = ids.filter((id) => !this.entities.some((entity) => isSameId(entity.id, id)));
    if (missingIds.length && !this.customSearch) {
      this.hydrateEntitiesByIds(missingIds, () => {
        if (this.hasSameIdSet(ids, this.pendingSelectedIds)) {
          this.applyPendingSelection(ids);
        }
      });
      return;
    }

    this.applyPendingSelection(ids);
  }

  private applyPendingSelection(ids: T['id'][]): void {
    this.mergePendingEntitiesIntoEntities();
    const requestedIds = this.castEntityIds(ids);
    const resolved = this.resolveEntitiesByIds(requestedIds);
    const resolvedIds = this.castEntityIds(resolved.map((entity) => entity.id));

    if (!this.hasSameIdSet(requestedIds, resolvedIds)) {
      this.pendingSelectedIds = resolvedIds;
      this.pendingSelectedEntities = [...resolved];
      this.setSelectedEntitiesSilently(resolved);
      this.selectedEntitiesChange.emit(resolved);
      return;
    }

    this.setSelectedEntitiesSilently(resolved);
  }

  private mergePendingEntitiesIntoEntities(): void {
    if (!this.pendingSelectedEntities.length) {
      return;
    }

    const missing = this.pendingSelectedEntities.filter(
      (pending) => !isNil(pending?.id) && !this.entities.some((entity) => isSameId(entity.id, pending.id))
    );
    if (missing.length) {
      this.entities = this.entities.concat(missing);
      this.entityCache.putEntities(this.entityCacheKind, missing);
    }
  }

  private setSelectedEntitiesSilently(entities: T[]): void {
    this.applyingPendingSelection = true;
    this._selectedEntities = [...entities];
    if (entities.length) {
      this.pendingSelectedIds = this.castEntityIds(entities.map((entity) => entity.id));
      this.pendingSelectedEntities = [...entities];
    } else {
      this.pendingSelectedIds = [];
      this.pendingSelectedEntities = [];
    }

    if (this.multiSelectComponent) {
      this.multiSelectComponent.syncOptions(this.entities);
      this.multiSelectComponent.setSelectionSilently(entities);
    }
    this.cdRef.markForCheck();

    // PrimeNG may reset ngModel after CD (after microtask suppress ends). Re-assert and
    // keep ignore-empty guard until that flush finishes so two-way bindings keep GET data.
    setTimeout(() => {
      if (this.pendingSelectedIds.length) {
        this.mergePendingEntitiesIntoEntities();
        const resolved = this.resolveEntitiesByIds(this.pendingSelectedIds);
        this._selectedEntities = [...resolved];
        if (this.multiSelectComponent) {
          this.multiSelectComponent.syncOptions(this.entities);
          this.multiSelectComponent.setSelectionSilently(resolved);
        }
        this.cdRef.markForCheck();
      }
      this.applyingPendingSelection = false;
    });
  }

  private resolveEntitiesByIds(ids: T['id'][]): T[] {
    return ids
      .map(
        (id) =>
          this.entities.find((entity) => isSameId(entity.id, id)) ??
          this.pendingSelectedEntities.find((entity) => isSameId(entity.id, id))
      )
      .filter(Boolean);
  }

  private castEntityIds(ids: T['id'][]): T['id'][] {
    if (!ids?.length) {
      return [];
    }

    if (!this.castToNumber) {
      return [...ids];
    }

    return ids.map((id) => (typeof id === 'string' ? (+id as T['id']) : id));
  }

  private hasSameIdSet(a: T['id'][], b: T['id'][]): boolean {
    if (a.length !== b.length) {
      return false;
    }

    return a.every((id) => b.some((other) => isSameId(id, other)));
  }
}
